import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { isHoldingGroup } from "../utils/activityBoardUtils";
import { dayOffSuffix } from "../../shared utils/personDetail";

const SLOT_OPTIONS = [
  { value: "AFTERNOON", label: "Afternoon" },
  { value: "EVENING", label: "Evening" },
  { value: "DAY", label: "Daytime" },
];

function normaliseValue(value, fallback = "") {
  return value === null || value === undefined ? fallback : value;
}

// Only staff carrying the ACTIVITY_LEADER role may lead an activity group.
// `roles` arrives as a delimited string (e.g. "TEACHER,ACTIVITY_LEADER").
function isActivityLeader(leader) {
  return String(leader?.roles || "")
    .split(/[,\s]+/)
    .map((r) => r.trim().toUpperCase())
    .includes("ACTIVITY_LEADER");
}

export default function AddEditActivityDialog({
  open,
  onClose,
  campDate,
  slot,
  campuses = [],
  leaders = [],
  products = [],
  group,
  existingGroups = [],
  onSave,
  disabled = false,
}) {
  const isEdit = Boolean(group?.groupId);

  // Arrivers/Departers holding groups are recognised by their name prefix, so
  // editing one mustn't rename it off that prefix — lock the name field.
  const lockName = isEdit && isHoldingGroup(group);

  const campusOptions = useMemo(
    () => (campuses || []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [campuses]
  );

  const [formData, setFormData] = useState({
    name: "",
    campus: "",
    slot: slot || "AFTERNOON",
    capacity: "",
    status: "PLANNED",
    leaderStaffId: "",
    leaderName: "",
    productId: "",
    productName: "",
    notes: "",
  });

  useEffect(() => {
    if (!open) return;

    setFormData({
      name: normaliseValue(group?.name),
      campus: normaliseValue(group?.campus),
      slot: normaliseValue(group?.slot, slot || "AFTERNOON"),
      capacity: normaliseValue(group?.capacity),
      status: normaliseValue(group?.status, "PLANNED"),
      leaderStaffId: normaliseValue(group?.leaderStaffId),
      leaderName: normaliseValue(group?.leaderName),
      productId: normaliseValue(group?.productId),
      productName: normaliseValue(group?.productName),
      notes: normaliseValue(group?.notes),
    });
  }, [open, group, slot]);

  const filteredLeaders = useMemo(() => {
    const byCampus = !formData.campus
      ? (leaders || [])
      : (leaders || []).filter((leader) => leader.campus === formData.campus);
    // Restrict the choices to staff with the ACTIVITY_LEADER role.
    return byCampus.filter(isActivityLeader);
  }, [leaders, formData.campus]);

  const filteredProducts = useMemo(() => {
    if (!formData.campus) return products;
    return (products || []).filter((product) => product.campus === formData.campus);
  }, [products, formData.campus]);

  const selectedLeader = useMemo(() => {
    return (
      filteredLeaders.find(
        (leader) => String(leader.staffId) === String(formData.leaderStaffId)
      ) || null
    );
  }, [filteredLeaders, formData.leaderStaffId]);

  // Map each staffId to the other groups they're already leading that day
  // (excluding the group being edited), so the dropdown can flag staff who are
  // already committed and help avoid over-relying on one person.
  const leaderLoad = useMemo(() => {
    const slotLabel = (s) => SLOT_OPTIONS.find((o) => o.value === s)?.label || s || "";
    const map = new Map();
    const currentId = group?.groupId;
    for (const g of existingGroups || []) {
      if (currentId != null && String(g.groupId) === String(currentId)) continue;
      const sid = g.leaderStaffId;
      if (sid === null || sid === undefined || sid === "") continue;
      const key = String(sid);
      const name = g.name || g.productName || "Activity";
      const full = g.slot ? `${name} (${slotLabel(g.slot)})` : name;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ name, full });
    }
    return map;
  }, [existingGroups, group?.groupId]);

  // A group may already carry a leader who isn't (or no longer is) an activity
  // leader (legacy data). Surface that rather than silently dropping it.
  const currentLeaderIsValid =
    formData.leaderStaffId === "" ||
    formData.leaderStaffId == null ||
    filteredLeaders.some(
      (leader) => String(leader.staffId) === String(formData.leaderStaffId)
    );

  const selectedProduct = useMemo(() => {
    return (
      filteredProducts.find(
        (product) => String(product.productId) === String(formData.productId)
      ) || null
    );
  }, [filteredProducts, formData.productId]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === "campus") {
        next.leaderStaffId = "";
        next.leaderName = "";
        next.productId = "";
        next.productName = "";
      }

      return next;
    });
  };

  const handleLeaderChange = (event) => {
    const value = event.target.value;
    const leader =
      filteredLeaders.find((item) => String(item.staffId) === String(value)) || null;

    setFormData((prev) => ({
      ...prev,
      leaderStaffId: value,
      leaderName: leader?.name || "",
    }));
  };

  const handleProductChange = (event) => {
    const value = event.target.value;
    const product =
      filteredProducts.find((item) => String(item.productId) === String(value)) || null;

    setFormData((prev) => ({
      ...prev,
      productId: value,
      productName: product?.productName || "",
    }));
  };

  const handleSave = async () => {
    const trimmedName = String(formData.name || "").trim();
    const parsedCapacity = Number(formData.capacity);

    if (!trimmedName) return;
    if (
      formData.capacity === "" ||
      formData.capacity === null ||
      Number.isNaN(parsedCapacity) ||
      parsedCapacity <= 0
    ) {
      return;
    }

    const payload = {
      name: trimmedName,
      campus: String(formData.campus || "").trim() || null,
      slot: formData.slot || "AFTERNOON",
      dates: campDate ? [campDate] : [],
      capacity: parsedCapacity,
      leaderStaffId:
        formData.leaderStaffId === "" || formData.leaderStaffId === null
          ? null
          : Number(formData.leaderStaffId),
      productId:
        formData.productId === "" || formData.productId === null
          ? null
          : Number(formData.productId),
      notes: String(formData.notes || "").trim() || null,
      activityKind: group?.activityKind || "PRODUCT",
      ratioChildrenPerAdult:
        group?.ratioChildrenPerAdult === null || group?.ratioChildrenPerAdult === undefined
          ? 0
          : Number(group.ratioChildrenPerAdult),
      };

    await onSave?.({
      mode: isEdit ? "edit" : "add",
      id: group?.groupId,
      payload,
    });
  };

  const trimmedName = String(formData.name || "").trim();
  const hasValidCapacity =
    formData.capacity !== "" &&
    formData.capacity !== null &&
    !Number.isNaN(Number(formData.capacity)) &&
    Number(formData.capacity) > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? "Edit activity group" : "Add activity group"}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Date
            </Typography>
            <Typography variant="body1">{campDate || "-"}</Typography>
          </Stack>

          <TextField
            label="Group name"
            value={formData.name}
            onChange={handleChange("name")}
            fullWidth
            required
            disabled={disabled || lockName}
            helperText={
              lockName
                ? "Arrivers/Departers group name is fixed so it stays recognised."
                : undefined
            }
          />

          <FormControl fullWidth disabled={disabled}>
            <InputLabel>Campus</InputLabel>
            <Select
              label="Campus"
              value={formData.campus}
              onChange={handleChange("campus")}
            >
              <MenuItem value="">
                <em>Select campus…</em>
              </MenuItem>
              {campusOptions.map((campus) => (
                <MenuItem key={campus.id} value={campus.name}>
                  {campus.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={disabled}>
            <InputLabel>Slot</InputLabel>
            <Select
              label="Slot"
              value={formData.slot}
              onChange={handleChange("slot")}
            >
              {SLOT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Capacity"
            type="number"
            value={formData.capacity}
            onChange={handleChange("capacity")}
            fullWidth
            required
            inputProps={{ min: 1 }}
            disabled={disabled}
          />

          <FormControl fullWidth disabled={disabled}>
            <InputLabel>Leader</InputLabel>
            <Select
              label="Leader"
              value={formData.leaderStaffId}
              onChange={handleLeaderChange}
              renderValue={(value) => {
                if (value === "" || value === null || value === undefined) {
                  return <em>No leader</em>;
                }
                const match = filteredLeaders.find(
                  (l) => String(l.staffId) === String(value)
                );
                if (match) return `${match.name}${dayOffSuffix(match)}`;
                return formData.leaderName || `Staff ${value}`;
              }}
            >
              <MenuItem value="">
                <em>No leader</em>
              </MenuItem>
              {filteredLeaders.map((leader) => {
                const others = leaderLoad.get(String(leader.staffId)) || [];
                return (
                  <MenuItem
                    key={leader.staffId}
                    value={leader.staffId}
                    title={
                      others.length
                        ? `Also leading: ${others.map((o) => o.full).join(", ")}`
                        : undefined
                    }
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ width: "100%" }}
                    >
                      <span style={{ flex: 1 }}>
                        {leader.name}
                        {dayOffSuffix(leader)}
                      </span>
                      {others.length > 0 && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ opacity: 0.7 }}
                        >
                          leading {others.map((o) => o.name).join(", ")}
                        </Typography>
                      )}
                    </Stack>
                  </MenuItem>
                );
              })}
              {!currentLeaderIsValid && (
                <MenuItem value={formData.leaderStaffId}>
                  {(formData.leaderName || "Current leader")} — not an activity leader
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {!currentLeaderIsValid && (
            <Typography variant="caption" color="warning.main">
              The current leader is not an activity leader. Please reassign to someone
              with the Activity Leader role.
            </Typography>
          )}

          {selectedLeader && (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Staff ID: {selectedLeader.staffId}
            </Typography>
          )}

          <FormControl fullWidth disabled={disabled}>
            <InputLabel>Linked product</InputLabel>
            <Select
              label="Linked product"
              value={formData.productId}
              onChange={handleProductChange}
            >
              <MenuItem value="">
                <em>No linked product</em>
              </MenuItem>
              {filteredProducts.map((product) => (
                <MenuItem key={product.productId} value={product.productId}>
                  {product.productName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedProduct && (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Product ID: {selectedProduct.productId}
              {selectedProduct.requiresApprovalForUnbookedAssignment
                ? " · Approval required for unbooked assignment"
                : ""}
            </Typography>
          )}

          <TextField
            label="Notes"
            value={formData.notes}
            onChange={handleChange("notes")}
            fullWidth
            multiline
            minRows={3}
            disabled={disabled}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={disabled}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={disabled || !trimmedName || !hasValidCapacity}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}